// Core of the shared contract: request and record schemas, chat tools, and the domain rules of the models
// behind the runners' llama-swap (MiniMax-H3 modes and limits, sizes, time estimates). Everything the UI shows
// about a model's limits comes from here, so both sides agree.
import { z } from "zod";

// ── Assets ────────────────────────────────────────────────────────────────────────────────────────────

/** What a file is; documents (PDFs) are only read by the assistant, never used by a model. */
export const MediaKind = z.enum(["image", "video", "audio", "document"]);
export type MediaKind = z.infer<typeof MediaKind>;

export const AssetSource = z.enum(["upload", "generation", "frame"]);
export type AssetSource = z.infer<typeof AssetSource>;

export const Asset = z.object({
  id: z.string(),
  kind: MediaKind,
  mime: z.string(),
  /** GET this to download or play the file. */
  url: z.string(),
  /**
   * GET this for the whole of what the model made, when `url` is a copy made for browsers: a video above
   * 1920×1080 behind its copy within that size, a WAV behind its M4A. Null when `url` is all there is.
   */
  originalUrl: z.string().nullable().optional(),
  /**
   * Size of that original when it is a video, as it is displayed (rotation applied). `width` and `height`
   * describe the copy at `url`, which is never above 1920×1080; this is the size of what was uploaded or made.
   */
  originalWidth: z.number().int().nullable().optional(),
  originalHeight: z.number().int().nullable().optional(),
  /** Still image for grids: the image itself, a video poster, a document's first page, or null for audio. */
  thumbUrl: z.string().nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  durationSec: z.number().nullable(),
  hasAudio: z.boolean(),
  source: AssetSource,
  /** Job that produced it (source = generation). */
  jobId: z.string().nullable(),
  /** Video a frame was taken from (source = frame). */
  parentAssetId: z.string().nullable(),
  /** Prompt of the generation, for search and "edit". */
  prompt: z.string().nullable(),
  /**
   * What the model was given when a prompt upsampler rewrote `prompt` first (FLUX.3 Klein); null or absent
   * otherwise. `prompt` stays what the person typed.
   */
  revisedPrompt: z.string().nullable().optional(),
  /** File name as uploaded, or null. */
  name: z.string().nullable(),
  /** Page count of a document, else null. */
  pages: z.number().int().nullable(),
  favorite: z.boolean(),
  createdAt: z.string(),
});
export type Asset = z.infer<typeof Asset>;

export const AssetPage = z.object({ items: z.array(Asset), nextCursor: z.string().nullable() });
export type AssetPage = z.infer<typeof AssetPage>;

/** Which file of an asset a signed link opens. */
export const LinkVariant = z.enum(["file", "original", "thumb"]);
export type LinkVariant = z.infer<typeof LinkVariant>;

/** POST /api/assets/:id/link */
export const CreateAssetLinkBody = z.object({
  variant: LinkVariant.default("file"),
  /** How long the link works: 1 minute to 7 days (default 1 hour). */
  ttlSec: z
    .number()
    .int()
    .min(60, "Links last at least a minute.")
    .max(7 * 86400, "Links last at most 7 days.")
    .default(3600),
});
export type CreateAssetLinkBody = z.infer<typeof CreateAssetLinkBody>;

/** A file path on the studio with `exp` and `sig` that opens that one file without signing in until expiresAt. */
export const AssetLink = z.object({ url: z.string(), variant: LinkVariant, expiresAt: z.string() });
export type AssetLink = z.infer<typeof AssetLink>;

export const CreateUploadLinkBody = z.object({
  /** How long the link works: 1 minute to 1 day (default 1 hour). */
  ttlSec: z
    .number()
    .int()
    .min(60, "Links last at least a minute.")
    .max(86400, "Upload links last at most a day.")
    .default(3600),
});
export type CreateUploadLinkBody = z.infer<typeof CreateUploadLinkBody>;

/** A path on the studio with `user`, `exp` and `sig`: POST a file there to add it to that person's library. */
export const UploadLink = z.object({ url: z.string(), expiresAt: z.string() });
export type UploadLink = z.infer<typeof UploadLink>;

// ── Speed: skipping denoising steps ───────────────────────────────────────────────────────────────────

/**
 * How a diffusion job spends its steps. "fast" lets sd-server's step cache (TaylorSeer and the like) skip about
 * half of the denoising steps, at a small, scene-dependent cost in fine detail; "quality" runs every step. Only a
 * GPU server whose config gives the model a fast path runs fast; anywhere else a fast job runs every step, and
 * the job says so (Job.ranSpeed). A request that leaves it out runs every step, so API clients that never heard
 * of it get what they always got.
 */
export const SpeedMode = z.enum(["fast", "quality"]);
export type SpeedMode = z.infer<typeof SpeedMode>;

// ── Images: Z-Image-Turbo, Qwen-Image 2.1 (and its Turbo), FLUX.2 klein and FLUX.3 Klein ───────────────────────────────

export const ImageModel = z.enum([
  "z-image",
  "qwen-image-2.1",
  "qwen-image-2.1-turbo",
  "flux2-klein-9b",
  "flux2-klein-base-fast-9b",
  "flux2-klein-base-9b",
  "flux3-klein",
]);
export type ImageModel = z.infer<typeof ImageModel>;
/**
 * What a request that names no model gets from the REST API (older clients, scripts). The web app starts here too.
 * The assistant and the MCP server pick PREFERRED_IMAGE_MODEL instead.
 */
export const DEFAULT_IMAGE_MODEL: ImageModel = "z-image";
/**
 * The picture model the chat assistant and the MCP server use when the caller does not pick one: Qwen-Image 2.1
 * follows a prompt most closely and writes legible text, and it is the one that edits the most pictures at once.
 */
export const PREFERRED_IMAGE_MODEL: ImageModel = "qwen-image-2.1";
/**
 * Picture models in the order an agent should consider them (and the order tool descriptions list them): the
 * preferred one and its 8-step Turbo, FLUX.3 Klein, then the fast one, then FLUX.2 klein from quick to exact. When the preferred model is on none of
 * a person's GPU servers, `defaultImageModel` takes the first one here that is.
 */
export const IMAGE_MODEL_PREFERENCE: readonly ImageModel[] = [
  "qwen-image-2.1",
  "qwen-image-2.1-turbo",
  "flux3-klein",
  "z-image",
  "flux2-klein-9b",
  "flux2-klein-base-fast-9b",
  "flux2-klein-base-9b",
];

/**
 * What each picture model can do and roughly what it costs, measured on LXC 100 at 1024×1024 over
 * two cards. Z-Image-Turbo is the fast one; Qwen-Image 2.1 is slower but follows a prompt more
 * closely and writes text; FLUX.2 klein 9B is the one for skin and photographic realism. The last two
 * can edit pictures you give them, `maxReferences` at a time.
 */
export const IMAGE_MODELS: Record<
  ImageModel,
  {
    label: string;
    note: string;
    /** What to pick it for, in words for the assistant and agents (tool descriptions); `note` is the UI's. */
    bestFor: string;
    secPerImage: number;
    secPerReference: number;
    references: boolean;
    maxReferences: number;
    /** Runs with real guidance, so a negative prompt has something to push against; the distilled ones ignore it. */
    negativePrompt: boolean;
  }
> = {
  "z-image": {
    label: "Z-Image Turbo",
    note: "Fast, 8 steps",
    bestFor: "quick drafts and many variations of a plain scene, the fastest of all",
    secPerImage: 15,
    secPerReference: 0,
    references: false,
    maxReferences: 0,
    negativePrompt: false,
  },
  "qwen-image-2.1": {
    label: "Qwen-Image 2.1",
    note: "Sharper text, can edit a picture",
    bestFor:
      "following a long, detailed prompt closely and writing legible text, signs and logos into the picture; edits that change, fix or restyle pictures",
    // Measured at 69 s from text and 164 s with one reference on the production pair.
    secPerImage: 70,
    secPerReference: 95,
    references: true,
    maxReferences: 10,
    negativePrompt: false,
  },
  "qwen-image-2.1-turbo": {
    label: "Qwen-Image 2.1 Turbo",
    note: "Qwen-Image in 8 steps: text and edits, about 5× faster",
    bestFor:
      "the same work as qwen-image-2.1 (legible text, signs and logos, long prompts, edits of pictures) at about a fifth of the time; the first try and quick iterations, with qwen-image-2.1 kept for the final version",
    // The checkpoint Qwen distilled to 8 steps without CFG (2026-10-09), on its own saved sigmas. One RTX 3090,
    // cold: 14 s at 1024×1024 from text (base 45 s), 55 s for a 1696×2528 poster (base 213 s), 77 s for a
    // 2048×2048 edit from one reference (base 271 s); small print as sharp and as correct as from the base.
    secPerImage: 18,
    secPerReference: 25,
    references: true,
    maxReferences: 10,
    negativePrompt: false,
  },
  "flux2-klein-9b": {
    label: "FLUX.2 klein 9B",
    note: "Realistic photos and skin, fast edits",
    bestFor:
      "realistic people, faces, skin and anything meant to look like a photograph; quick edits of photos",
    // Distilled to 4 steps: 11 s from text on the production pair, 8 s on the RTX 5090, an edit about the same.
    secPerImage: 12,
    secPerReference: 4,
    references: true,
    // Black Forest Labs document multi-reference editing for klein with up to four pictures.
    maxReferences: 4,
    negativePrompt: false,
  },
  "flux2-klein-base-fast-9b": {
    label: "FLUX.2 klein base · fast",
    note: "About a minute: close to base, takes Avoid",
    bestFor: "keeping a product reference nearly as exactly as klein base, in about a minute",
    // klein base with half of the base-to-turbo LoRA, 8 steps at guidance 2. On the RTX 5090: 23 s from text,
    // 57 s with two references (base: 49 s and 118 s); the production pair takes about twice as long.
    secPerImage: 40,
    secPerReference: 35,
    references: true,
    maxReferences: 4,
    negativePrompt: true,
  },
  "flux2-klein-base-9b": {
    label: "FLUX.2 klein base 9B",
    note: "Slow and exact: final shots, product detail",
    bestFor:
      "final shots that must reproduce a product reference exactly (stitching, labels, colours); minutes per picture, never the first try",
    // Undistilled: 20 steps with guidance, two passes each. With two references at 1152×1536 it took 225 s on
    // the production pair and 118 s on the RTX 5090; from text alone about a third of that.
    secPerImage: 75,
    secPerReference: 70,
    references: true,
    maxReferences: 4,
    negativePrompt: true,
  },
  "flux3-klein": {
    label: "FLUX.3 Klein",
    note: "Up to 4K, edits from up to 10 pictures",
    bestFor:
      "large pictures up to 4K (3840×2160), photographs and illustrations from short prompts, and edits from up to 10 pictures; its prompt upsampler turns a few words into a full description first",
    // Distilled to 16 steps without CFG. On one RTX 3090: 12 s of sampling at 1024×1024, 56 s at 2048×2048 and
    // 138 s at 3840×2160; the RTX 5090 takes about half. The prompt upsampler adds 3-6 s.
    secPerImage: 25,
    secPerReference: 5,
    references: true,
    // Black Forest Labs: up to ten references; the first sets the framing.
    maxReferences: 10,
    negativePrompt: false,
  },
};

/**
 * Models whose request goes through Black Forest Labs' prompt upsampler first (flux3-klein-pup, on the same GPU
 * server): FLUX.3 Klein was trained on its long captions. On unless a request sets `upsample: false`.
 */
export function imageModelUpsamples(model: ImageModel): boolean {
  return model === "flux3-klein";
}

/**
 * Models whose prompts Qwen's prompt experts (qwen-image-2.1-pe-t2i / -i2i) rewrite: they were trained for
 * Qwen-Image 2.1, and the Turbo is the same model in fewer steps. The UI offers "improve prompt" for these.
 */
export function imageModelHasPromptExperts(model: ImageModel): boolean {
  return model === "qwen-image-2.1" || model === "qwen-image-2.1-turbo";
}

/** Models that can take a picture to work from, so the UI knows when to offer the attach button. */
export function imageModelTakesReferences(model: ImageModel): boolean {
  return IMAGE_MODELS[model].references;
}

/** Whether the model listens to a negative prompt (only the ones that run with guidance do). */
export function imageModelTakesNegativePrompt(model: ImageModel): boolean {
  return IMAGE_MODELS[model].negativePrompt;
}

/**
 * What a realistic picture should not be, used when a request leaves `negativePrompt` out: the ways these models
 * go wrong on people (spare limbs and fingers, skin like plastic) and the usual marks of a cheap render.
 * An empty string in the request means "none". Mirrored in apps/runner/internal/contract/core.go.
 */
export const IMAGE_DEFAULT_NEGATIVE_PROMPT =
  "extra legs, extra arms, extra feet, extra fingers, fused or missing fingers, deformed hands, distorted anatomy, plastic skin, waxy skin, airbrushed, oversmoothed, doll-like face, cartoon, illustration, 3d render, oversaturated, blurry, low quality, jpeg artifacts, watermark, text, logo";

/** How many pictures one edit may work from on this model; 0 when it takes none. */
export function maxImageReferences(model: ImageModel): number {
  return IMAGE_MODELS[model].maxReferences;
}

export const ImageAspect = z.enum(["1:1", "16:9", "9:16", "4:3", "3:4", "3:2", "2:3"]);
export type ImageAspect = z.infer<typeof ImageAspect>;

/** Z-Image works on multiples of 64 around one megapixel. */
export const IMAGE_SIZES: Record<ImageAspect, { width: number; height: number }> = {
  "1:1": { width: 1024, height: 1024 },
  "16:9": { width: 1344, height: 768 },
  "9:16": { width: 768, height: 1344 },
  "4:3": { width: 1152, height: 896 },
  "3:4": { width: 896, height: 1152 },
  "3:2": { width: 1152, height: 768 },
  "2:3": { width: 768, height: 1152 },
};

/** The most any model takes (Qwen Image 2.1); each model's own limit is `maxImageReferences(model)`. */
export const MAX_IMAGE_REFERENCES = 10;
/** Per-model canvas budgets. Qwen's official largest preset is 2400x1792 (about 2048x2048). */
export const IMAGE_MODEL_MAX_PIXELS: Record<ImageModel, number> = {
  "z-image": 2_200_000,
  "qwen-image-2.1": 2_400 * 1_792,
  "qwen-image-2.1-turbo": 2_400 * 1_792,
  // FLUX.2 works up to four megapixels; 2048x2048 was run on both machines.
  "flux2-klein-9b": 2_048 * 2_048,
  "flux2-klein-base-9b": 2_048 * 2_048,
  "flux2-klein-base-fast-9b": 2_048 * 2_048,
  // 4K: 3840x2160, and the same area in the other shapes.
  "flux3-klein": 3_840 * 2_160,
};

/** Common sizes only; Qwen's final entry for each shape is its official full-resolution preset. */
export const IMAGE_SIZE_OPTIONS: Record<ImageAspect, readonly { width: number; height: number }[]> = {
  "1:1": [512, 768, 1024, 1280, 1536, 2048, 2880].map((side) => ({ width: side, height: side })),
  "16:9": [
    { width: 896, height: 512 },
    { width: 1344, height: 768 },
    { width: 1792, height: 1024 },
    { width: 2048, height: 1152 },
    { width: 2752, height: 1536 },
    { width: 3840, height: 2160 },
  ],
  "9:16": [
    { width: 512, height: 896 },
    { width: 768, height: 1344 },
    { width: 1024, height: 1792 },
    { width: 1152, height: 2048 },
    { width: 1536, height: 2752 },
    { width: 2160, height: 3840 },
  ],
  "4:3": [
    { width: 768, height: 576 },
    { width: 1024, height: 768 },
    { width: 1152, height: 896 },
    { width: 1536, height: 1152 },
    { width: 2048, height: 1536 },
    { width: 2400, height: 1792 },
    { width: 3264, height: 2448 },
  ],
  "3:4": [
    { width: 576, height: 768 },
    { width: 768, height: 1024 },
    { width: 896, height: 1152 },
    { width: 1152, height: 1536 },
    { width: 1536, height: 2048 },
    { width: 1792, height: 2400 },
    { width: 2448, height: 3264 },
  ],
  "3:2": [
    { width: 768, height: 512 },
    { width: 1152, height: 768 },
    { width: 1536, height: 1024 },
    { width: 1920, height: 1280 },
    { width: 2304, height: 1536 },
    { width: 2528, height: 1696 },
    { width: 3456, height: 2304 },
  ],
  "2:3": [
    { width: 512, height: 768 },
    { width: 768, height: 1152 },
    { width: 1024, height: 1536 },
    { width: 1280, height: 1920 },
    { width: 1536, height: 2304 },
    { width: 1696, height: 2528 },
    { width: 2304, height: 3456 },
  ],
};

function imageSizeOptions(model: ImageModel, aspect: ImageAspect) {
  const maxPixels = IMAGE_MODEL_MAX_PIXELS[model];
  const maxSide = imageSideMax(model);
  const k = imageSideMultiple(model);
  return IMAGE_SIZE_OPTIONS[aspect].filter(
    (size) =>
      size.width * size.height <= maxPixels &&
      Math.max(size.width, size.height) <= maxSide &&
      size.width % k === 0 &&
      size.height % k === 0,
  );
}

/** Pair a selected common side with its matching canvas preset. */
export function imageSizeFromSide(
  aspect: ImageAspect,
  side: "width" | "height",
  pixels: number,
  model: ImageModel = DEFAULT_IMAGE_MODEL,
): { width: number; height: number } {
  const options = imageSizeOptions(model, aspect);
  const exact = options.find((size) => size[side] === pixels);
  if (exact) return { ...exact };
  return { ...IMAGE_SIZES[aspect] };
}

/** Popular values offered by one resolution dropdown for this model and shape. */
export function imageDimensionOptions(
  aspect: ImageAspect,
  side: "width" | "height",
  model: ImageModel = DEFAULT_IMAGE_MODEL,
): number[] {
  return imageSizeOptions(model, aspect).map((size) => size[side]);
}

/** Bounds of an exact picture size (`width` × `height`), per side: the widest any model takes (FLUX.3 Klein's 4K). */
export const IMAGE_SIDE_MIN = 512;
export const IMAGE_SIDE_MAX = 3840;

/** The longest side this model makes: 3840 for FLUX.3 Klein, 2752 (Qwen's widest preset) for the others. */
export function imageSideMax(model: ImageModel): number {
  return model === "flux3-klein" ? 3840 : 2752;
}

/** What an exact width and height must be a multiple of: 64 for Z-Image, 16 for FLUX.3 Klein, 32 for the others. */
export function imageSideMultiple(model: ImageModel): number {
  if (model === "z-image") return 64;
  return model === "flux3-klein" ? 16 : 32;
}

const megapixels = (pixels: number) => (pixels / 1_000_000).toFixed(1);
const joinWith = (items: string[], last: string) =>
  items.length <= 1 ? (items[0] ?? "") : `${items.slice(0, -1).join(", ")} ${last} ${items.at(-1)}`;

/** The models that take reference pictures, each with its limit: "qwen-image-2.1 (up to 10), … or …". */
export function referenceModelsText(minReferences = 1): string {
  return joinWith(
    IMAGE_MODEL_PREFERENCE.filter((id) => IMAGE_MODELS[id].maxReferences >= minReferences).map(
      (id) => `${id} (up to ${IMAGE_MODELS[id].maxReferences})`,
    ),
    "or",
  );
}

/** How many reference pictures each model takes, grouped: "up to 10 with qwen-image-2.1, up to 4 with a, b or c". */
export function referenceLimitsText(): string {
  const byLimit = new Map<number, ImageModel[]>();
  for (const id of IMAGE_MODEL_PREFERENCE) {
    const max = IMAGE_MODELS[id].maxReferences;
    if (max > 0) byLimit.set(max, [...(byLimit.get(max) ?? []), id]);
  }
  return [...byLimit]
    .sort(([a], [b]) => b - a)
    .map(([max, ids]) => `up to ${max} with ${joinWith(ids, "or")}`)
    .join(", ");
}

/** The models that make pictures from text only. */
export function textOnlyImageModels(): ImageModel[] {
  return IMAGE_MODEL_PREFERENCE.filter((id) => IMAGE_MODELS[id].maxReferences === 0);
}

/**
 * The model to use when the caller names none: PREFERRED_IMAGE_MODEL, unless `offered` (the image models of the
 * GPU servers the person may use, as /api/capabilities lists them) is known and lacks it; then the first model in
 * IMAGE_MODEL_PREFERENCE that is offered and takes this many reference pictures.
 */
export function defaultImageModel(references = 0, offered?: readonly string[] | null): ImageModel {
  if (!offered?.length || offered.includes(PREFERRED_IMAGE_MODEL)) return PREFERRED_IMAGE_MODEL;
  const fits = (id: ImageModel) => references === 0 || IMAGE_MODELS[id].maxReferences >= references;
  return IMAGE_MODEL_PREFERENCE.find((id) => offered.includes(id) && fits(id)) ?? PREFERRED_IMAGE_MODEL;
}

/**
 * One line per picture model for tool descriptions (the MCP server's generate_image): what to pick it for, what it
 * takes, how long it takes and how large it goes, all read from IMAGE_MODELS so a description cannot drift.
 */
export function imageModelLines(): string[] {
  return IMAGE_MODEL_PREFERENCE.map((id) => {
    const m = IMAGE_MODELS[id];
    const refs =
      m.maxReferences > 0
        ? `edits or works from up to ${m.maxReferences} reference pictures`
        : "text only: takes no reference pictures";
    const time = `about ${m.secPerImage} s a picture${m.secPerReference ? ` plus ${m.secPerReference} s per reference` : ""}`;
    const size = `up to ${megapixels(IMAGE_MODEL_MAX_PIXELS[id])} MP, sides multiples of ${imageSideMultiple(id)}`;
    const neg = m.negativePrompt ? "takes a negative prompt" : "no negative prompt";
    return `${id}${id === PREFERRED_IMAGE_MODEL ? " (default)" : ""} — ${m.label}, best for ${m.bestFor}. ${refs[0]?.toUpperCase()}${refs.slice(1)}; ${neg}; ${time}; ${size}.`;
  });
}

/**
 * Why a picture model cannot make a request with these inputs, one sentence per problem that names the limit and
 * what to do instead; empty when it can. The contract's validation (ImageRequest), the MCP server and the chat all
 * use it, so a person, the assistant and an agent get the same answer.
 */
export function imageRequestProblems(r: {
  model: ImageModel;
  /** How many reference pictures. */
  references?: number;
  negativePrompt?: string | null;
  width?: number;
  height?: number;
}): string[] {
  const m = IMAGE_MODELS[r.model];
  if (!m) return [];
  const name = `${m.label} (${r.model})`;
  const out: string[] = [];
  const { width: w, height: h } = r;
  if ((w === undefined) !== (h === undefined)) out.push("Give both width and height, or neither.");
  else if (w !== undefined && h !== undefined) {
    const maxSide = imageSideMax(r.model);
    if ([w, h].some((s) => s < IMAGE_SIDE_MIN || s > maxSide))
      out.push(`Width and height must each be ${IMAGE_SIDE_MIN}–${maxSide} px; ${w}×${h} is not.`);
    const k = imageSideMultiple(r.model);
    if (w % k !== 0 || h % k !== 0) {
      const round = (s: number) => Math.min(maxSide, Math.max(IMAGE_SIDE_MIN, Math.round(s / k) * k));
      out.push(
        `${name} needs width and height in multiples of ${k}: ${round(w)}×${round(h)} instead of ${w}×${h}.`,
      );
    }
    const max = IMAGE_MODEL_MAX_PIXELS[r.model];
    if (w * h > max)
      out.push(
        `${w}×${h} (${megapixels(w * h)} MP) is too large for ${name}, which makes at most ${megapixels(max)} MP. Pick a smaller size.`,
      );
  }
  const n = r.references ?? 0;
  if (n > 0 && m.maxReferences === 0) {
    out.push(
      `${name} makes pictures from text only and cannot work from reference pictures. To edit or work from pictures, pick a model that takes them: ${referenceModelsText()}.`,
    );
  } else if (n > m.maxReferences) {
    const more = referenceModelsText(n);
    out.push(
      `${name} works from at most ${m.maxReferences} reference pictures at a time, not ${n}. ${more ? `Use fewer, or ${more}.` : "Use fewer."}`,
    );
  }
  if (r.negativePrompt?.trim() && !m.negativePrompt) {
    const takers = IMAGE_MODEL_PREFERENCE.filter((id) => IMAGE_MODELS[id].negativePrompt);
    out.push(
      `${name} does not take a negative prompt; ${joinWith(takers, "and")} do. Leave it out and say what you want in the prompt.`,
    );
  }
  return out;
}

export const PromptEnhanceRequest = z.object({
  prompt: z.string().trim().min(1).max(4000),
  referenceImageAssetIds: z.array(z.string()).max(MAX_IMAGE_REFERENCES).default([]),
});
export type PromptEnhanceRequest = z.infer<typeof PromptEnhanceRequest>;

export const PromptEnhanceResponse = z.object({
  prompt: z.string().min(1),
  /** Shape recommended by the prompt expert, when it emitted a known ratio. */
  aspect: ImageAspect.nullable(),
  /** For edits, the source image whose framing the expert asks the renderer to keep. */
  ratioFollow: z.number().int().min(0).nullable(),
});
export type PromptEnhanceResponse = z.infer<typeof PromptEnhanceResponse>;

export const ImageRequest = z
  .object({
    type: z.literal("image"),
    model: ImageModel.default(DEFAULT_IMAGE_MODEL),
    prompt: z.string().trim().min(1).max(4000),
    aspect: ImageAspect.default("1:1"),
    /** What to steer away from. Left out: the realism default. Only models with `negativePrompt` take one. */
    negativePrompt: z.string().max(2000).optional(),
    /** Assets the model edits or draws on; empty for plain text to image. */
    referenceImageAssetIds: z.array(z.string()).max(MAX_IMAGE_REFERENCES).default([]),
    /** Number of images, each a separate seed. */
    count: z.number().int().min(1).max(4).default(1),
    seed: z.number().int().optional(),
    /**
     * Exact size for API callers (e.g. 1088×1920 start frames for vertical 1080p video); overrides `aspect`.
     * IMAGE_SIDE_MIN–IMAGE_SIDE_MAX per side in multiples of imageSideMultiple(model), within the model's
     * IMAGE_MODEL_MAX_PIXELS (checked below, with sentences that name the limit).
     */
    width: z.number().int().optional(),
    height: z.number().int().optional(),
    /** Left out: every step (see SpeedMode). Only models a GPU server has a fast path for can skip any. */
    speed: SpeedMode.optional(),
    /**
     * Run the prompt through the model's prompt upsampler first (imageModelUpsamples). Left out: on. Black Forest
     * Labs say FLUX.3 Klein is noticeably weaker without it; false sends the prompt as it is.
     */
    upsample: z.boolean().optional(),
  })
  .superRefine((r, ctx) => {
    const problems = imageRequestProblems({
      model: r.model,
      references: r.referenceImageAssetIds.length,
      negativePrompt: r.negativePrompt,
      width: r.width,
      height: r.height,
    });
    for (const message of problems) ctx.addIssue({ code: "custom", message });
  });
export type ImageRequest = z.infer<typeof ImageRequest>;

// ── Video: MiniMax-H3 ─────────────────────────────────────────────────────────────────────────────────

export const VIDEO_FPS = 24;
/** Latent-token budget of the two 24 GB cards; the server refuses larger jobs. */
export const VIDEO_TOKEN_BUDGET = 130_000;
export const MAX_REFERENCE_IMAGES = 9;
export const MAX_REFERENCE_VIDEOS = 3;
export const MAX_REFERENCE_AUDIOS = 3;
export const MAX_REFERENCE_FILES = 12;
/** Reference videos and audio clips must each last 2–15 s, at most 15 s together per kind. */
export const REFERENCE_CLIP_MIN_SEC = 2;
export const REFERENCE_CLIP_MAX_SEC = 15;

export const VideoResolution = z.enum(["480p", "720p", "1080p", "2k"]);
export type VideoResolution = z.infer<typeof VideoResolution>;
export const VideoAspect = z.enum(["16:9", "9:16", "1:1"]);
export type VideoAspect = z.infer<typeof VideoAspect>;

/** Sides are multiples of 32, as the model requires. */
export const VIDEO_SIZES: Record<VideoResolution, Record<VideoAspect, { width: number; height: number }>> = {
  "480p": {
    "16:9": { width: 832, height: 480 },
    "9:16": { width: 480, height: 832 },
    "1:1": { width: 640, height: 640 },
  },
  "720p": {
    "16:9": { width: 1248, height: 704 },
    "9:16": { width: 704, height: 1248 },
    "1:1": { width: 960, height: 960 },
  },
  "1080p": {
    "16:9": { width: 1920, height: 1088 },
    "9:16": { width: 1088, height: 1920 },
    "1:1": { width: 1440, height: 1440 },
  },
  "2k": {
    "16:9": { width: 2560, height: 1440 },
    "9:16": { width: 1440, height: 2560 },
    "1:1": { width: 1920, height: 1920 },
  },
};

/** H3 takes 17k+5 frames. These are the lengths offered in the UI. */
export const VIDEO_FRAME_OPTIONS = [39, 73, 107, 141, 175, 243, 362] as const;

export function isValidFrameCount(frames: number): boolean {
  return Number.isInteger(frames) && frames >= 5 && (frames - 5) % 17 === 0;
}

export function framesToSeconds(frames: number): number {
  return frames / VIDEO_FPS;
}

/** Nearest valid frame count for a duration in seconds. */
export function secondsToFrames(seconds: number): number {
  const k = Math.max(0, Math.round((seconds * VIDEO_FPS - 5) / 17));
  return 17 * k + 5;
}

export function videoTokens(width: number, height: number, frames: number): number {
  const k = (frames - 5) / 17;
  return (width / 32) * (height / 32) * (5 * k + 2);
}

/** Longest valid frame count that fits the token budget at this size. */
export function maxFramesForSize(width: number, height: number): number {
  let k = 0;
  while (videoTokens(width, height, 17 * (k + 1) + 5) <= VIDEO_TOKEN_BUDGET && k < 21) k++;
  return 17 * k + 5;
}

export const VideoQuality = z.enum(["fast", "standard", "high"]);
export type VideoQuality = z.infer<typeof VideoQuality>;
export const VIDEO_STEPS: Record<VideoQuality, number> = { fast: 12, standard: 20, high: 30 };

/**
 * How H3 is driven, derived from what is attached — the person never picks a checkpoint:
 * nothing → text; a first and/or last frame → the FL2VA checkpoint; any reference → Ref2VA.
 */
export const VideoMode = z.enum(["text", "first_frame", "last_frame", "first_last", "references"]);
export type VideoMode = z.infer<typeof VideoMode>;

export const VideoRequest = z
  .object({
    type: z.literal("video"),
    prompt: z.string().trim().min(1).max(4000),
    negativePrompt: z.string().max(2000).optional(),
    resolution: VideoResolution.default("720p"),
    aspect: VideoAspect.default("16:9"),
    frames: z.number().int().default(73),
    quality: VideoQuality.default("standard"),
    /** Left out: every step (see SpeedMode). */
    speed: SpeedMode.optional(),
    seed: z.number().int().optional(),
    firstFrameAssetId: z.string().optional(),
    lastFrameAssetId: z.string().optional(),
    referenceImageAssetIds: z.array(z.string()).max(MAX_REFERENCE_IMAGES).default([]),
    referenceVideoAssetIds: z.array(z.string()).max(MAX_REFERENCE_VIDEOS).default([]),
    referenceAudioAssetIds: z.array(z.string()).max(MAX_REFERENCE_AUDIOS).default([]),
  })
  .superRefine((v, ctx) => {
    if (!isValidFrameCount(v.frames)) {
      ctx.addIssue({ code: "custom", path: ["frames"], message: "Video length must be 17k+5 frames." });
    }
    const refs =
      v.referenceImageAssetIds.length + v.referenceVideoAssetIds.length + v.referenceAudioAssetIds.length;
    if (refs > MAX_REFERENCE_FILES) {
      ctx.addIssue({
        code: "custom",
        path: ["referenceImageAssetIds"],
        message: "Use at most 12 reference files.",
      });
    }
    if (refs > 0 && (v.firstFrameAssetId || v.lastFrameAssetId)) {
      ctx.addIssue({
        code: "custom",
        path: ["firstFrameAssetId"],
        message: "Use either start/end frames or references, not both.",
      });
    }
    const { width, height } = VIDEO_SIZES[v.resolution][v.aspect];
    if (videoTokens(width, height, v.frames) > VIDEO_TOKEN_BUDGET) {
      const max = maxFramesForSize(width, height);
      ctx.addIssue({
        code: "custom",
        path: ["frames"],
        message: `Too long for ${v.resolution}: up to ${framesToSeconds(max).toFixed(1)} s fits.`,
      });
    }
  });
export type VideoRequest = z.infer<typeof VideoRequest>;

export function deriveVideoMode(
  v: Pick<
    VideoRequest,
    | "firstFrameAssetId"
    | "lastFrameAssetId"
    | "referenceImageAssetIds"
    | "referenceVideoAssetIds"
    | "referenceAudioAssetIds"
  >,
): VideoMode {
  const refs =
    (v.referenceImageAssetIds?.length ?? 0) +
    (v.referenceVideoAssetIds?.length ?? 0) +
    (v.referenceAudioAssetIds?.length ?? 0);
  if (refs > 0) return "references";
  if (v.firstFrameAssetId && v.lastFrameAssetId) return "first_last";
  if (v.firstFrameAssetId) return "first_frame";
  if (v.lastFrameAssetId) return "last_frame";
  return "text";
}

/** sd-server preset for a mode. */
export function videoPresetForMode(mode: VideoMode): "h3-fl2va" | "h3-ref2va" {
  return mode === "references" ? "h3-ref2va" : "h3-fl2va";
}

export const VIDEO_MODE_LABELS: Record<VideoMode, { title: string; hint: string }> = {
  text: { title: "Text to video", hint: "Describe the scene; the model also writes the sound." },
  first_frame: { title: "Animate a photo", hint: "Your image becomes the first frame." },
  last_frame: { title: "End on a frame", hint: "The video arrives at your image." },
  first_last: { title: "Frame to frame", hint: "The video moves from the first image to the second." },
  references: { title: "From references", hint: "People, objects, clips and sounds guide the video." },
};

// ── Music: audio.cpp ──────────────────────────────────────────────────────────────────────────────────

export const MusicModel = z.enum(["minimax-music3"]);
export type MusicModel = z.infer<typeof MusicModel>;

export const MusicRequest = z.object({
  type: z.literal("music"),
  model: MusicModel.default("minimax-music3"),
  /** Genre, mood, instruments, voice — "warm indie folk, acoustic guitar, soft female vocal". */
  style: z.string().trim().min(1).max(1000),
  /** Section-tagged lyrics ([Verse], [Chorus]); empty with instrumental = true. */
  lyrics: z.string().max(6000).default(""),
  instrumental: z.boolean().default(false),
  durationSec: z.number().int().min(10).max(240).default(60),
  seed: z.number().int().optional(),
});
export type MusicRequest = z.infer<typeof MusicRequest>;

// ── Speech: audio.cpp, Qwen3-TTS ──────────────────────────────────────────────────────────────────────

/**
 * Three Qwen3-TTS checkpoints, one per way of choosing a voice. They are separate weights, so the mode
 * decides which llama-swap model the job runs on (see modelForRequest).
 *
 *   speaker  a voice packaged in the CustomVoice checkpoint, steered by an instruction
 *   clone    a voice copied from reference audio: a registered voice, or an uploaded asset
 *   design   a voice invented from a written description
 */
export const SpeechMode = z.enum(["speaker", "clone", "design"]);
export type SpeechMode = z.infer<typeof SpeechMode>;

export const SPEECH_MODEL_FOR_MODE: Record<SpeechMode, string> = {
  speaker: "qwen3-tts-custom",
  clone: "qwen3-tts",
  design: "qwen3-tts-design",
};

/** Voices baked into the CustomVoice checkpoint (its config's spk_id map). Two of them speak a dialect. */
export const SpeechSpeaker = z.enum([
  "vivian",
  "serena",
  "ryan",
  "aiden",
  "eric",
  "dylan",
  "uncle_fu",
  "ono_anna",
  "sohee",
]);
export type SpeechSpeaker = z.infer<typeof SpeechSpeaker>;

export const SPEECH_SPEAKER_LABELS: Record<SpeechSpeaker, { title: string; hint: string }> = {
  vivian: { title: "Vivian", hint: "Woman, bright and even" },
  serena: { title: "Serena", hint: "Woman, warm and unhurried" },
  ryan: { title: "Ryan", hint: "Man, calm and clear" },
  aiden: { title: "Aiden", hint: "Man, younger and lively" },
  eric: { title: "Eric", hint: "Man, Sichuan dialect" },
  dylan: { title: "Dylan", hint: "Man, Beijing dialect" },
  uncle_fu: { title: "Uncle Fu", hint: "Older man, storyteller" },
  ono_anna: { title: "Ono Anna", hint: "Woman, Japanese" },
  sohee: { title: "Sohee", hint: "Woman, Korean" },
};

/** The ten languages Qwen3-TTS is trained on; "auto" lets the model read it off the text. */
export const SpeechLanguage = z.enum(["auto", "en", "zh", "ja", "ko", "de", "fr", "ru", "pt", "es", "it"]);
export type SpeechLanguage = z.infer<typeof SpeechLanguage>;

/**
 * Qwen3-TTS names its languages in full rather than by code, and rejects anything else outright
 * ("unsupported language: ru"), so the runner translates before it calls audio.cpp. "auto" is not in
 * the map on purpose: the field is left out of the request entirely and the model reads the language
 * off the text. The two dialects the model also knows (Beijing, Sichuan) are not languages you pick —
 * they come with the speakers Dylan and Eric, and only when the language is Chinese or unset.
 */
export const SPEECH_LANGUAGE_NAMES: Record<Exclude<SpeechLanguage, "auto">, string> = {
  en: "english",
  zh: "chinese",
  ja: "japanese",
  ko: "korean",
  de: "german",
  fr: "french",
  ru: "russian",
  pt: "portuguese",
  es: "spanish",
  it: "italian",
};

export const SPEECH_LANGUAGE_LABELS: Record<SpeechLanguage, string> = {
  auto: "Detect",
  en: "English",
  zh: "Chinese",
  ja: "Japanese",
  ko: "Korean",
  de: "German",
  fr: "French",
  ru: "Russian",
  pt: "Portuguese",
  es: "Spanish",
  it: "Italian",
};

/** Reference audio for a clone: long enough to carry a voice, short enough to stay a prompt. */
export const VOICE_REF_MIN_SEC = 3;
export const VOICE_REF_MAX_SEC = 30;
export const MAX_SPEECH_CHARS = 8000;

export const SpeechRequest = z
  .object({
    type: z.literal("speech"),
    mode: SpeechMode.default("speaker"),
    /** What to say. Long text is chunked by the model, so this is generous. */
    text: z.string().trim().min(1).max(MAX_SPEECH_CHARS),
    /** speaker mode: which packaged voice. */
    speaker: SpeechSpeaker.default("vivian"),
    /**
     * clone mode: a voice registered in the library. The server resolves it to voiceRefAssetId +
     * referenceText when the job is created, so a stored job still says exactly what it cloned.
     */
    voiceId: z.string().optional(),
    /** clone mode: an audio asset to clone directly, without registering a voice first. */
    voiceRefAssetId: z.string().optional(),
    /** clone mode: what the reference audio says. Optional, but it sharpens the clone. */
    referenceText: z.string().max(2000).default(""),
    /**
     * speaker mode: how to say it ("very happy", "whispering, conspiratorial").
     * design mode: who is speaking ("an elderly British narrator, warm and slow") — required there.
     */
    instruct: z.string().max(1000).default(""),
    language: SpeechLanguage.default("auto"),
    seed: z.number().int().optional(),
  })
  .superRefine((req, ctx) => {
    if (req.mode === "clone" && !req.voiceId && !req.voiceRefAssetId) {
      ctx.addIssue({
        code: "custom",
        path: ["voiceId"],
        message: "Cloning needs a voice from the library or a piece of reference audio.",
      });
    }
    if (req.mode === "design" && req.instruct.trim().length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["instruct"],
        message: "Designing a voice needs a description of it.",
      });
    }
  });
export type SpeechRequest = z.infer<typeof SpeechRequest>;

export const SPEECH_MODE_LABELS: Record<SpeechMode, { title: string; hint: string }> = {
  speaker: { title: "A packaged voice", hint: "Pick one of the nine Qwen voices and tell it how to speak." },
  clone: { title: "Clone a voice", hint: "From a voice you saved, or a few seconds of audio." },
  design: { title: "Design a voice", hint: "Describe a speaker and the model invents the voice." },
};

// ── Enhance: restoring and upscaling a picture or a video that already exists (asd.cpp) ───────────────

/**
 * Models that take a file and give back a better one: no prompt, frames in and frames out. Each works on one
 * kind of file, and the result is of that kind. The first model of a kind is the one a file of that kind starts
 * with (enhanceModelFor); the others are picked by the person (enhanceModelsFor).
 */
export const EnhanceModel = z.enum(["wonder-3.5", "denoise-max", "precise-2.6", "nyx-3", "hyperion-2.5"]);
export type EnhanceModel = z.infer<typeof EnhanceModel>;

/** Largest picture an enhancement writes (40 megapixels: four times 3072×3072). */
export const ENHANCE_IMAGE_MAX_PIXELS = 40_000_000;
/**
 * Largest video frame an enhancement writes: the area of 3840×2160, whatever the shape. This is the model's own
 * ceiling (asd.cpp enhance::Profile max_output_*): a larger request is not refused, it is written at this area.
 */
export const ENHANCE_VIDEO_MAX_PIXELS = 3840 * 2160;
/**
 * The video model writes no frame narrower or lower than this, whatever the scale and whichever way the video
 * is turned: an upright 1080×1920 clip comes back 1280 wide even at 1× (asd.cpp enhance::Profile min_output_*).
 */
export const ENHANCE_VIDEO_MIN_SIZE = { width: 1280, height: 720 } as const;
/** Frame rate assumed for estimates: an asset does not record its own, and what the studio makes is 24. */
export const ENHANCE_ASSUMED_FPS = 24;

/**
 * What each enhancement model works on and what it costs, measured on one RTX 3090 of LXC 100 (2026-10-11):
 * Wonder 3.5 writes 2688×1536 in 5 s and 4096×4096 in 20 s; Precise 2.6 turns three seconds of 1280×720 into
 * 2560×1440 in 220 s and of 848×480 into 1696×960 in 94 s (twice as fast on the RTX 5090). Measured there on
 * 2026-10-13: Denoise Max, which is Wonder's transformer with other weights, writes 1024×1024 in 1.2 s and
 * 4096×4096 in 20 s; Hyperion 2.5 converts three seconds of 1920×1080 in 97 s and of 1512×2688 in 218 s, and 34
 * frames of 3840×2160 in 203 s with 14.9 GB at the peak (70, 160 s on the RTX 5090: half its time is the tone
 * map on the processor). Measured there on 2026-10-14: Nyx 3, a small network that works every frame over in
 * tiles and generates nothing, clears three seconds of 848×480 in 9 s and of 1920×1080 in 53 s, and writes them
 * twice as large in 11 and 76 s (1.1 GB; 7 and 39 s on the RTX 5090).
 */
export const ENHANCE_MODELS: Record<
  EnhanceModel,
  {
    label: string;
    /** What it does, as the name of a choice next to the other models of its kind. */
    does: string;
    /** The same as the start of a sentence about one job: "Restore", "Remove noise", "Convert to HDR". */
    action: string;
    note: string;
    /** The kind of file it takes and gives back. */
    input: "image" | "video";
    /** Output sizes the UI offers, relative to the source; 1 restores without enlarging. A single one: no choice. */
    scales: readonly number[];
    /**
     * The result is an HDR video (BT.2020 PQ, 10-bit HEVC) of the size of the source. The studio keeps it as
     * the asset's original and shows a copy brought down to standard range where HDR cannot be shown.
     */
    hdr?: boolean;
    /**
     * A video model that writes the source times the scale as it is: it has no smallest frame it enlarges to
     * (ENHANCE_VIDEO_MIN_SIZE) and no ceiling it shrinks to, and its time follows the frames it reads, not the
     * ones it writes.
     */
    exact?: boolean;
    /** Most pixels of one frame it writes. */
    maxPixels: number;
    /** Seconds per megapixel written (read, for an exact model; per frame, for video), and the fixed part of a run. */
    secPerMegapixel: number;
    secBase: number;
  }
> = {
  "wonder-3.5": {
    label: "Wonder 3.5",
    does: "Restore",
    action: "Restore",
    note: "Restores a picture and enlarges it up to four times: sharper detail, cleaner edges, less noise.",
    input: "image",
    scales: [1, 2, 3, 4],
    maxPixels: ENHANCE_IMAGE_MAX_PIXELS,
    secPerMegapixel: 1.2,
    secBase: 2,
  },
  "denoise-max": {
    label: "Denoise Max",
    does: "Denoise",
    action: "Remove noise",
    note: "Clears a picture of heavy noise and grain, and enlarges it up to four times.",
    input: "image",
    scales: [1, 2, 3, 4],
    maxPixels: ENHANCE_IMAGE_MAX_PIXELS,
    secPerMegapixel: 1.2,
    secBase: 2,
  },
  "precise-2.6": {
    label: "Precise 2.6",
    does: "Restore",
    action: "Restore",
    note: "Restores a video and enlarges it up to 4K, frame-consistent; the sound is kept as it is.",
    input: "video",
    scales: [1, 1.5, 2, 3, 4],
    maxPixels: ENHANCE_VIDEO_MAX_PIXELS,
    secPerMegapixel: 0.8,
    secBase: 8,
  },
  "nyx-3": {
    label: "Nyx 3",
    does: "Denoise",
    action: "Remove noise",
    note: "Clears a video of noise and compression damage without redrawing it, at its own size or twice as large; the sound is kept as it is.",
    input: "video",
    scales: [1, 2],
    exact: true,
    maxPixels: ENHANCE_VIDEO_MAX_PIXELS,
    secPerMegapixel: 0.38,
    secBase: 4,
  },
  "hyperion-2.5": {
    label: "Hyperion 2.5",
    does: "HDR",
    action: "Convert to HDR",
    note: "Turns a standard video into HDR at its own size; the sound is kept as it is.",
    input: "video",
    scales: [1],
    hdr: true,
    maxPixels: ENHANCE_VIDEO_MAX_PIXELS,
    secPerMegapixel: 0.7,
    secBase: 8,
  },
};

/** The enhancement model a file of this kind starts with, or null when none works on it. */
export function enhanceModelFor(kind: MediaKind): EnhanceModel | null {
  return enhanceModelsFor(kind)[0] ?? null;
}

/** Every enhancement model that works on a file of this kind, the usual one first. */
export function enhanceModelsFor(kind: MediaKind): EnhanceModel[] {
  return EnhanceModel.options.filter((m) => ENHANCE_MODELS[m].input === kind);
}

/** What a stored enhancement did, for a title: "Restore and enlarge 2×", "Remove noise", "Convert to HDR". */
export function enhanceActionLabel(model: EnhanceModel, scale: number): string {
  const m = ENHANCE_MODELS[model];
  return scale > 1 ? `${m.action} and enlarge ${scale}×` : m.action;
}

/**
 * Size of the result of an enhancement. For a video this is the model's own arithmetic (asd.cpp
 * enhance::output_size, what sd-server's enhance_size answers as the delivered size): the scale, then at least
 * 720 high, then at least 1280 wide, then at most the area of 3840×2160, both sides even. So what the studio
 * shows before a job is what comes back. A picture is the source times the scale. An HDR conversion keeps the
 * size of the source, an odd side losing its last line to the encoder. An `exact` model writes the source times
 * the scale, each side brought down to an even number (asd.cpp's detail task).
 */
export function enhanceOutputSize(
  model: EnhanceModel,
  width: number,
  height: number,
  scale: number,
): { width: number; height: number } {
  let w = width * scale;
  let h = height * scale;
  if (ENHANCE_MODELS[model].hdr && w > 0 && h > 0) {
    return { width: Math.floor(width / 2) * 2, height: Math.floor(height / 2) * 2 };
  }
  if (ENHANCE_MODELS[model].exact && w > 0 && h > 0) {
    return { width: Math.floor(w / 2) * 2, height: Math.floor(h / 2) * 2 };
  }
  if (ENHANCE_MODELS[model].input !== "video" || !(w > 0) || !(h > 0)) {
    return { width: Math.round(w), height: Math.round(h) };
  }
  if (h < ENHANCE_VIDEO_MIN_SIZE.height) {
    w = (ENHANCE_VIDEO_MIN_SIZE.height / h) * w;
    h = ENHANCE_VIDEO_MIN_SIZE.height;
  }
  if (w < ENHANCE_VIDEO_MIN_SIZE.width) {
    h = (ENHANCE_VIDEO_MIN_SIZE.width / w) * h;
    w = ENHANCE_VIDEO_MIN_SIZE.width;
  }
  if (w * h > ENHANCE_VIDEO_MAX_PIXELS) {
    const ratio = Math.sqrt(ENHANCE_VIDEO_MAX_PIXELS / (w * h));
    w *= ratio;
    h *= ratio;
  }
  const even = (x: number) => Math.floor(x / 2 + 0.5) * 2;
  return { width: even(w), height: even(h) };
}

/**
 * The size of the file an enhancement works from: a video's original when the studio has one (what was uploaded
 * or what the model made), otherwise the file itself. Scales are relative to this.
 */
export function enhanceSourceSize(a: {
  kind: MediaKind;
  width: number | null;
  height: number | null;
  originalWidth?: number | null;
  originalHeight?: number | null;
}): { width: number | null; height: number | null } {
  if (a.kind === "video" && a.originalWidth && a.originalHeight) {
    return { width: a.originalWidth, height: a.originalHeight };
  }
  return { width: a.width, height: a.height };
}

/**
 * The scales a model offers for a source of this size, each with the size it gives. `same` marks a scale whose
 * result is no larger than that of a smaller one (the model's ceiling was already reached), which is not worth
 * offering: a 1512×2688 video is 2160×3840 at 1.5× and at everything above.
 */
export function enhanceScaleChoices(
  model: EnhanceModel,
  width: number,
  height: number,
): { scale: number; width: number; height: number; same: boolean }[] {
  let previous = 0;
  return ENHANCE_MODELS[model].scales.map((scale) => {
    const out = enhanceOutputSize(model, width, height, scale);
    const area = out.width * out.height;
    const same = previous > 0 && area <= previous;
    previous = Math.max(previous, area);
    return { scale, ...out, same };
  });
}

export const EnhanceRequest = z.object({
  type: z.literal("enhance"),
  model: EnhanceModel,
  /** The picture or video to restore, of the kind the model works on. */
  sourceAssetId: z.string().min(1),
  /** Output size relative to the source: 1 restores without enlarging. */
  scale: z.number().min(1).max(4).default(2),
  seed: z.number().int().optional(),
  /**
   * Size and length of the source. The studio fills these in from the file when the job is created (whatever
   * a caller sends is replaced), so limits and estimates can be worked out from the request alone, and the
   * stored job stays a truthful record after the source is deleted.
   */
  sourceWidth: z.number().int().positive().optional(),
  sourceHeight: z.number().int().positive().optional(),
  sourceDurationSec: z.number().positive().optional(),
  /**
   * Also filled in by the studio, for a video that has an original (what was uploaded or what the model made,
   * before the copy for browsers was made from it: re-encoded, and brought down to 1920×1080 when larger): the
   * GPU server is then given that file and not the copy. `sourceWidth` and `sourceHeight` are then the size of
   * the original too, so the scale is relative to it. (Jobs stored on 2026-10-11 kept the size of the copy
   * there; enhanceModelInput still reads them as they were meant.)
   */
  sourceOriginalWidth: z.number().int().positive().optional(),
  sourceOriginalHeight: z.number().int().positive().optional(),
});
export type EnhanceRequest = z.infer<typeof EnhanceRequest>;

/** The source of an enhancement as its stored request describes it. */
type EnhanceSource = Pick<
  EnhanceRequest,
  "scale" | "sourceWidth" | "sourceHeight" | "sourceOriginalWidth" | "sourceOriginalHeight"
>;

/**
 * What the model is given and the scale it is asked for. Without an original that is the file itself and the
 * scale of the request. With one, the model works from the original, and the scale is the request's taken
 * against the size of the copy the person saw: 2× of a 2560×1440 video shown as 1920×1080 asks the model for
 * 1.5×, and the result is the 3840×2160 the person was told. Never below 1: a video is not made smaller.
 */
export function enhanceModelInput(r: EnhanceSource): { width: number; height: number; scale: number } {
  if (r.sourceOriginalWidth && r.sourceOriginalHeight && r.sourceWidth) {
    return {
      width: r.sourceOriginalWidth,
      height: r.sourceOriginalHeight,
      scale: Math.max(1, (r.scale * r.sourceWidth) / r.sourceOriginalWidth),
    };
  }
  return { width: r.sourceWidth ?? 0, height: r.sourceHeight ?? 0, scale: r.scale };
}

/** Size of the frames a stored enhancement writes (0×0 while the request has no source size yet). */
export function enhanceRequestOutputSize(r: EnhanceSource & { model: EnhanceModel }): {
  width: number;
  height: number;
} {
  const input = enhanceModelInput(r);
  return enhanceOutputSize(r.model, input.width, input.height, input.scale);
}

/**
 * Why this file cannot go through this model at this scale, as a sentence for the person; null when it can.
 * The length of a video is no reason: it is taken at any length, and costs the time of its frames (estimateJobSec).
 */
export function enhanceProblem(r: {
  model: EnhanceModel;
  scale: number;
  kind: MediaKind;
  width: number | null;
  height: number | null;
}): string | null {
  const m = ENHANCE_MODELS[r.model];
  if (r.kind !== m.input) {
    const other = enhanceModelFor(r.kind);
    return other
      ? `${m.label} works on ${m.input === "image" ? "pictures" : "videos"}; use ${ENHANCE_MODELS[other].label} for this file.`
      : `${m.label} works on ${m.input === "image" ? "pictures" : "videos"}, not on this kind of file.`;
  }
  if (!r.width || !r.height) return "The size of this file is not known, so it cannot be enhanced.";
  const out = enhanceOutputSize(r.model, r.width, r.height, r.scale);
  if (out.width * out.height > m.maxPixels) {
    const fits = [...m.scales].reverse().find((s) => {
      const o = enhanceOutputSize(r.model, r.width as number, r.height as number, s);
      return o.width * o.height <= m.maxPixels;
    });
    const what = m.input === "image" ? "picture" : "video";
    return fits
      ? `At ${r.scale}× this ${what} would be ${out.width}×${out.height}, more than ${m.label} writes. Pick ${fits}×.`
      : `This ${what} is already larger than ${m.label} writes (${r.width}×${r.height}).`;
  }
  return null;
}

// ── Generation requests and jobs ──────────────────────────────────────────────────────────────────────

export const GenerationRequest = z.discriminatedUnion("type", [
  ImageRequest,
  VideoRequest,
  MusicRequest,
  SpeechRequest,
  EnhanceRequest,
]);
export type GenerationRequest = z.infer<typeof GenerationRequest>;
export type GenerationType = GenerationRequest["type"];

/** The speed a request asks for ("quality" when it says nothing); null for kinds without the choice. */
export function requestedSpeed(req: GenerationRequest): SpeedMode | null {
  if (req.type !== "image" && req.type !== "video") return null;
  return req.speed ?? "quality";
}

export const CreateJobBody = z.object({
  request: GenerationRequest,
  /** Remix/variation/extend lineage: the job this one continues. */
  parentJobId: z.string().optional(),
  /** Chat that asked for it (set by the server for tool calls). */
  chatId: z.string().optional(),
  /** Draft jobs wait for the person to press Start (chat plan cards). */
  draft: z.boolean().default(false),
});
export type CreateJobBody = z.infer<typeof CreateJobBody>;

export const JobStatus = z.enum([
  "draft",
  "queued",
  "loading_model",
  "running",
  "completed",
  "failed",
  "cancelled",
]);
export type JobStatus = z.infer<typeof JobStatus>;

export const JobProgress = z.object({
  /** "queued" | "loading model" | "generating" | "decoding" | "saving" — shown as is. */
  phase: z.string(),
  step: z.number().int().nullable(),
  totalSteps: z.number().int().nullable(),
});
export type JobProgress = z.infer<typeof JobProgress>;

export const Job = z.object({
  id: z.string(),
  type: z.enum(["image", "video", "music", "speech", "enhance"]),
  status: JobStatus,
  request: GenerationRequest,
  /** Video only: how H3 is driven for this request. */
  videoMode: VideoMode.nullable(),
  /** llama-swap model id the job runs on. */
  model: z.string(),
  progress: JobProgress.nullable(),
  /** Seconds until done from now, including a model load and jobs ahead. */
  etaSec: z.number().nullable(),
  queuePosition: z.number().int().nullable(),
  resultAssetIds: z.array(z.string()),
  error: z.string().nullable(),
  parentJobId: z.string().nullable(),
  chatId: z.string().nullable(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  /** Person who made it (jobs are only ever returned in full to their owner). */
  ownerId: z.string(),
  /** GPU server and slot that ran (or runs) it; null while queued. */
  runnerName: z.string().nullable(),
  slotId: z.string().nullable(),
  /**
   * What that server runs: "fast" (the step cache skips steps) or "quality" (every step). A fast request on a
   * server without a fast path runs every step and says "quality" here. Null while queued, and for kinds
   * without the choice.
   */
  ranSpeed: SpeedMode.nullable(),
});
export type Job = z.infer<typeof Job>;

// ── Model kinds, per-model limits, redacted jobs ──────────────────────────────────────────────────────

export const ModelKind = z.enum(["image", "video", "music", "speech", "enhance", "chat"]);
export type ModelKind = z.infer<typeof ModelKind>;

/**
 * What one model on one runner slot can do; missing fields mean "no limit beyond the contract's own".
 * Runners declare them in their config; the scheduler only sends a job to a slot whose limits allow it.
 */
export const ModelLimits = z.object({
  /** image: width × height. enhance: pixels of one frame that is written. */
  maxPixels: z.number().int().positive().optional(),
  /** video: H3 latent tokens (the contract's VIDEO_TOKEN_BUDGET is the absolute maximum). */
  maxVideoTokens: z.number().int().positive().optional(),
  /** video: highest resolution preset. */
  maxResolution: z.enum(["480p", "720p", "1080p", "2k"]).optional(),
  /** video: longest frame count. */
  maxFrames: z.number().int().positive().optional(),
  /** video at 1080p and 2K: most reference images (the vision tower sees them at canvas size). */
  maxRefImagesHighRes: z.number().int().min(0).optional(),
  /** music: longest song in seconds. enhance: longest video it takes. */
  maxDurationSec: z.number().positive().optional(),
});
export type ModelLimits = z.infer<typeof ModelLimits>;

/**
 * A job as other people see it in the shared queue: what kind of work and how big, where it stands, whose it is.
 * No prompt, no assets and no id that unlocks anything; `jobId` is only set on the viewer's own jobs.
 */
export const RedactedJob = z.object({
  /** Opaque, stable per job (for list keys); not accepted by any endpoint. */
  ref: z.string(),
  mine: z.boolean(),
  jobId: z.string().nullable(),
  type: z.enum(["image", "video", "music", "speech", "enhance"]),
  model: z.string(),
  status: JobStatus,
  videoMode: VideoMode.nullable(),
  resolution: VideoResolution.nullable(),
  aspect: z.string().nullable(),
  /** Length of the video or song. */
  durationSec: z.number().nullable(),
  /** Pictures in an image job. */
  count: z.number().int().nullable(),
  ownerName: z.string(),
  runnerName: z.string().nullable(),
  slotId: z.string().nullable(),
  queuePosition: z.number().int().nullable(),
  etaSec: z.number().nullable(),
  progress: JobProgress.nullable(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
});
export type RedactedJob = z.infer<typeof RedactedJob>;

// ── GPU state and events ──────────────────────────────────────────────────────────────────────────────

export const GpuState = z.object({
  /** llama-swap model currently loaded, null when nothing is. */
  activeModel: z.string().nullable(),
  state: z.enum(["idle", "loading", "ready", "busy"]),
  /** What holds the GPU right now. */
  busyWith: z.enum(["chat", "job"]).nullable(),
  jobId: z.string().nullable(),
  since: z.string(),
});
export type GpuState = z.infer<typeof GpuState>;

// ── Chats ─────────────────────────────────────────────────────────────────────────────────────────────

export const ChatThread = z.object({
  id: z.string(),
  title: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ChatThread = z.infer<typeof ChatThread>;

/** Tools the chat assistant can call. They queue a job and return at once; results arrive as job events. */
export const ChatToolInputs = {
  generate_image: z.object({
    prompt: z.string().describe("Detailed English description of the image."),
    aspect: ImageAspect.optional().describe("Aspect ratio; 1:1 when unsure."),
    count: z.number().int().min(1).max(4).optional().describe("How many variants, 1–4."),
    model: ImageModel.optional().describe(
      `Picture model, ${PREFERRED_IMAGE_MODEL} when left out. ${IMAGE_MODEL_PREFERENCE.map((id) => `${id}: best for ${IMAGE_MODELS[id].bestFor}`).join(". ")}.`,
    ),
    referenceAssetIds: z
      .array(z.string())
      .max(MAX_IMAGE_REFERENCES)
      .optional()
      .describe(
        `Pictures to edit or work from: ${referenceLimitsText()}. Not with ${joinWith(textOnlyImageModels(), "or")}, which makes pictures from text only. Say what to change in the prompt.`,
      ),
  }),
  generate_video: z.object({
    prompt: z
      .string()
      .describe(
        "English description of the subject, action and camera motion. Describe real objects precisely.",
      ),
    resolution: VideoResolution.optional(),
    aspect: VideoAspect.optional(),
    durationSec: z.number().min(1).max(15).optional().describe("Length in seconds, 3 when unsure."),
    firstFrameAssetId: z.string().optional().describe("Asset id of an image to use as the first frame."),
    lastFrameAssetId: z.string().optional().describe("Asset id of an image to use as the last frame."),
    referenceAssetIds: z
      .array(z.string())
      .optional()
      .describe("Asset ids of images, clips or sounds to use as references (people, objects, voice)."),
  }),
  generate_music: z.object({
    style: z.string().describe("Genre, mood, instruments and voice, in English."),
    lyrics: z
      .string()
      .optional()
      .describe("Lyrics with [Verse]/[Chorus] section tags; omit for instrumental."),
    instrumental: z.boolean().optional(),
    durationSec: z.number().int().min(10).max(240).optional(),
  }),
} as const;
export type ChatToolName = keyof typeof ChatToolInputs;

export const ChatToolOutput = z.object({
  jobId: z.string(),
  status: JobStatus,
  etaSec: z.number().nullable(),
  /** Short line the assistant can relay ("Queued, about 2 min"). */
  note: z.string(),
});
export type ChatToolOutput = z.infer<typeof ChatToolOutput>;

// ── Capabilities and estimates ────────────────────────────────────────────────────────────────────────

/**
 * What the viewer can make right now, over the GPU servers they may use (online or briefly offline).
 * `limits` is the widest any of those servers allows (null when unknown or unavailable).
 */
export const Capabilities = z.object({
  chat: z.object({ model: z.string(), vision: z.boolean(), available: z.boolean() }),
  image: z.object({
    models: z.array(ImageModel),
    available: z.boolean(),
    limits: ModelLimits.nullable(),
    /**
     * The same per model: whether an online server the viewer may use has it, and its widest limits there
     * (`limits` above mixes all picture models). Missing from studios older than this field.
     */
    byModel: z
      .array(z.object({ model: ImageModel, available: z.boolean(), limits: ModelLimits.nullable() }))
      .optional(),
  }),
  video: z.object({ model: z.string(), available: z.boolean(), limits: ModelLimits.nullable() }),
  music: z.object({ models: z.array(MusicModel), available: z.boolean(), limits: ModelLimits.nullable() }),
  /** `modes` are the ways of picking a voice whose checkpoint this fleet can actually serve. */
  speech: z.object({
    modes: z.array(SpeechMode),
    speakers: z.array(SpeechSpeaker),
    available: z.boolean(),
    limits: ModelLimits.nullable(),
  }),
  /**
   * Restoring and upscaling: the models some GPU server of the viewer has, each with whether one is online and
   * its widest limits there. Missing from studios older than this field.
   */
  enhance: z
    .object({
      models: z.array(EnhanceModel),
      available: z.boolean(),
      byModel: z.array(
        z.object({ model: EnhanceModel, available: z.boolean(), limits: ModelLimits.nullable() }),
      ),
    })
    .optional(),
  /**
   * Models an online GPU server the viewer may use can run fast (see SpeedMode), with the largest time factor
   * among those servers: a fast job takes about that share of the full time.
   */
  fast: z.array(z.object({ model: z.string(), timeFactor: z.number() })),
});
export type Capabilities = z.infer<typeof Capabilities>;

/** Seconds a model takes to load when it is not the one on the GPU (measured on this host). */
export const MODEL_LOAD_SEC: Record<string, number> = {
  "qwen3.8-27b": 20,
  "z-image": 12,
  // 7.3 GB transformer, 9.4 GB text encoder and a 1.3 GB VAE to read in.
  "qwen-image-2.1": 25,
  "qwen-image-2.1-turbo": 25,
  // 18 GB transformer and a 16 GB text encoder, read into RAM and handed to the card in turn.
  "flux2-klein-9b": 20,
  "flux2-klein-base-9b": 20,
  "flux2-klein-base-fast-9b": 20,
  // 11 GB transformer, 8 GB text encoder and its 10 GB prompt upsampler, which loads beside it.
  "flux3-klein": 25,
  "minimax-h3": 45,
  "minimax-music3": 30,
  // 4.2 GB of bf16 each, and measured swapping in on LXC 100 at 2.7-4.3 s including a short generation.
  // 10 covers reading the weights off a cold disk rather than out of the page cache.
  "qwen3-tts": 10,
  "qwen3-tts-custom": 10,
  "qwen3-tts-design": 10,
  // 6.5 GB of transformer each; they share one sd-server command, which loads one in a few seconds.
  "wonder-3.5": 10,
  "denoise-max": 10,
  "precise-2.6": 10,
  "hyperion-2.5": 10,
  // 140 MB of two small networks.
  "nyx-3": 3,
};

/**
 * Generation time without loading, fitted to measurements on the two RTX 3090:
 * 832×480×39 f ≈ 54 s and 2560×1440×73 f ≈ 25 min at 20 steps.
 */
export function estimateVideoSec(width: number, height: number, frames: number, steps: number): number {
  const tokens = videoTokens(width, height, frames);
  return Math.round(20 + 4.33e-4 * tokens ** 1.334 * (steps / 20));
}

/**
 * Seconds for one FLUX.3 Klein picture of this many megapixels, fitted on one RTX 3090 (2026-10-16, 16 steps, sage
 * attention): sampling 12.4 s at 1 MP, 56 s at 4.2 MP, 138 s at 8.3 MP (linear in the GEMMs, quadratic in the
 * attention), plus the prompt upsampler, the text encoder and a decode that goes to tiles above 2K.
 */
export function estimateFlux3Sec(megapixels: number): number {
  return Math.round(8 + 13.2 * megapixels + 0.58 * megapixels ** 2);
}

export function estimateJobSec(request: GenerationRequest): number {
  switch (request.type) {
    case "image": {
      const m = IMAGE_MODELS[request.model];
      const perReference = m.secPerReference * request.referenceImageAssetIds.length;
      if (request.model === "flux3-klein") {
        const size =
          request.width && request.height
            ? { width: request.width, height: request.height }
            : IMAGE_SIZES[request.aspect];
        return (estimateFlux3Sec((size.width * size.height) / 1e6) + perReference) * request.count;
      }
      return (m.secPerImage + perReference) * request.count;
    }
    case "video": {
      const { width, height } = VIDEO_SIZES[request.resolution][request.aspect];
      return estimateVideoSec(width, height, request.frames, VIDEO_STEPS[request.quality]);
    }
    case "music":
      return Math.round(15 + 2.5 * request.durationSec);
    // Measured on one RTX 3090: bf16 Qwen3-TTS runs about 6x real time, and speech is roughly
    // 15 characters a second. A model load dominates anything short, hence the floor.
    case "speech":
      return Math.round(5 + request.text.length / 15 / 6);
    case "enhance": {
      const m = ENHANCE_MODELS[request.model];
      // A job is stored with the size of its source; a request that has none yet is estimated as 1280×720 for 3 s.
      const out = enhanceRequestOutputSize({
        ...request,
        sourceWidth: request.sourceWidth ?? 1280,
        sourceHeight: request.sourceHeight ?? 720,
      });
      const frames =
        m.input === "video" ? Math.ceil((request.sourceDurationSec ?? 3) * ENHANCE_ASSUMED_FPS) : 1;
      // An exact model works on the frames it reads, in tiles: twice as large costs it under half as much again.
      const input = enhanceModelInput({
        ...request,
        sourceWidth: request.sourceWidth ?? 1280,
        sourceHeight: request.sourceHeight ?? 720,
      });
      const pixels = m.exact
        ? input.width * input.height * (input.scale > 1.2 ? 1.4 : 1)
        : out.width * out.height;
      return Math.round(m.secBase + (m.secPerMegapixel * pixels * frames) / 1e6);
    }
  }
}

export function modelForRequest(request: GenerationRequest): string {
  switch (request.type) {
    case "image":
      return request.model;
    case "video":
      return "minimax-h3";
    case "music":
      return request.model;
    case "speech":
      return SPEECH_MODEL_FOR_MODE[request.mode];
    case "enhance":
      return request.model;
  }
}

// ── Voice library ─────────────────────────────────────────────────────────────────────────────────────

/**
 * A saved voice: a name over a piece of reference audio kept in the asset library. Registering one is
 * what turns "here is a wav" into something you can ask for by name later, from the web UI or an agent.
 */
export const Voice = z.object({
  id: z.string(),
  name: z.string(),
  /** The audio asset the clone is taken from. */
  assetId: z.string(),
  /** What that audio says; empty when it was never transcribed. */
  referenceText: z.string(),
  language: SpeechLanguage,
  durationSec: z.number().nullable(),
  /** A short sample of this voice reading something, made on demand; null until one exists. */
  previewAssetId: z.string().nullable(),
  ownerId: z.string(),
  createdAt: z.string(),
});
export type Voice = z.infer<typeof Voice>;

export const VOICE_NAME_MAX = 60;

export const CreateVoiceBody = z.object({
  name: z.string().trim().min(1).max(VOICE_NAME_MAX),
  /** An audio asset already in the library — upload it first through POST /api/uploads. */
  assetId: z.string(),
  referenceText: z.string().max(2000).default(""),
  language: SpeechLanguage.default("auto"),
});
export type CreateVoiceBody = z.infer<typeof CreateVoiceBody>;

export const UpdateVoiceBody = z.object({
  name: z.string().trim().min(1).max(VOICE_NAME_MAX).optional(),
  referenceText: z.string().max(2000).optional(),
  language: SpeechLanguage.optional(),
});
export type UpdateVoiceBody = z.infer<typeof UpdateVoiceBody>;

// ── Input types (fields with defaults are optional) ───────────────────────────────────────────────────

export type ImageRequestInput = z.input<typeof ImageRequest>;
export type VideoRequestInput = z.input<typeof VideoRequest>;
export type MusicRequestInput = z.input<typeof MusicRequest>;
export type SpeechRequestInput = z.input<typeof SpeechRequest>;
export type EnhanceRequestInput = z.input<typeof EnhanceRequest>;
export type GenerationRequestInput = z.input<typeof GenerationRequest>;
export type CreateJobInput = z.input<typeof CreateJobBody>;
export type CreateVoiceInput = z.input<typeof CreateVoiceBody>;
